import { ReactNode } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { STATUS_ACCENT } from "@/pages/tina/items/components/status-accent";
import type { ItemStatus } from "@/types/tina";

interface Props {
  status: ItemStatus;
  title?: string;
  subtitle?: ReactNode;
  headerRight?: ReactNode;
  children: ReactNode;
}

export function PanelShell({
  status,
  title,
  subtitle,
  headerRight,
  children,
}: Props) {
  const accent = STATUS_ACCENT[status];
  const Icon = accent.icon;
  const resolvedTitle = title ?? accent.label;
  return (
    <Card className="relative overflow-hidden">
      <span
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-y-0 left-0 w-1",
          accent.stripe,
        )}
      />
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0 pb-3 pl-7">
        <div className="flex items-start gap-3">
          <span
            className={cn(
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-md",
              accent.iconWrap,
            )}
          >
            <Icon
              className={cn(
                "h-4 w-4",
                accent.iconColor,
                accent.iconSpin && "animate-spin",
              )}
              strokeWidth={2.25}
            />
          </span>
          <div className="flex flex-col gap-1">
            <CardTitle className="text-base font-semibold tracking-tight">
              {resolvedTitle}
            </CardTitle>
            {subtitle && (
              <p className="text-xs text-muted-foreground">{subtitle}</p>
            )}
          </div>
        </div>
        {headerRight && (
          <div className="flex shrink-0 items-start">{headerRight}</div>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-4 pb-6 pl-7">
        {children}
      </CardContent>
    </Card>
  );
}
